import React, { useState, useEffect } from 'react';
import {
  Typography,
  Container,
  Box,
  Grid,
  Paper,
  Card,
  CardContent,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  alpha,
  useTheme
} from '@mui/material';
import { Refresh, TrendingUp, MonetizationOn, Person, CreditCard } from '@mui/icons-material';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title } from 'chart.js';
import { Doughnut, Line, Bar } from 'react-chartjs-2';
// import { collection, query, where, getDocs, Timestamp, orderBy } from 'firebase-shim';
// import { db } from '../firebase/config';
import * as indexedDBService from '../utils/indexedDBService';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/formatters';
import AnimatedPage from '../components/AnimatedPage';

// Register ChartJS components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title
);

interface SalesByMonth {
  month: string;
  amount: number;
}

interface SalesByClient {
  clientName: string;
  amount: number;
}

interface PaymentStatus {
  status: string;
  amount: number;
}

const SalesInsights: React.FC = () => {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('6');
  const [salesByMonth, setSalesByMonth] = useState<SalesByMonth[]>([]);
  const [salesByClient, setSalesByClient] = useState<SalesByClient[]>([]);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus[]>([]);
  const [totalSales, setTotalSales] = useState(0);
  const [averageSale, setAverageSale] = useState(0);
  const [topClient, setTopClient] = useState('');
  const [outstandingAmount, setOutstandingAmount] = useState(0);

  useEffect(() => {
    if (currentUser) {
      fetchSalesData();
    }
  }, [currentUser, period]);

  const fetchSalesData = async () => {
    setLoading(true);
    try {
      // Calculate start date based on selected period
      const now = new Date();
      const monthsToSubtract = parseInt(period);
      const startDate = new Date();
      startDate.setMonth(now.getMonth() - monthsToSubtract);
      startDate.setDate(1);
      startDate.setHours(0, 0, 0, 0);

      const allInvoices = await indexedDBService.getAllItems<any>('invoices');

      const invoices = allInvoices.filter(invoice => {
        if (invoice.userId !== currentUser?.uid) return false;

        // Handle both Firestore Timestamp and regular Date strings/objects
        let invoiceDate: Date;
        if (invoice.date && typeof invoice.date.toDate === 'function') {
          invoiceDate = new Date(invoice.date);
        } else if (invoice.date) {
          invoiceDate = new Date(invoice.date);
        } else {
          return false;
        }

        return invoiceDate >= startDate;
      }).sort((a, b) => {
        const dateA = a.date ? new Date(a.date) : new Date(0);
        const dateB = b.date ? new Date(b.date) : new Date(0);
        return dateA.getTime() - dateB.getTime();
      });

      // Process data for charts
      const monthData: Record<string, number> = {};
      const clientData: Record<string, number> = {};
      const statusData: Record<string, number> = {};
      let totalAmount = 0;
      let invoiceCount = 0;
      let pendingAmount = 0;

      invoices.forEach(invoice => {
        const amount = invoice.totalAmount || 0;

        // Month data
        let date: Date;
        if (invoice.date && typeof invoice.date.toDate === 'function') {
          date = new Date(invoice.date);
        } else {
          date = new Date(invoice.date || Date.now());
        }

        const monthYear = `${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`;
        monthData[monthYear] = (monthData[monthYear] || 0) + amount;

        // Client data
        const clientName = invoice.clientName || 'Unknown';
        clientData[clientName] = (clientData[clientName] || 0) + amount;

        // Status data
        const status = invoice.paymentStatus || 'Unpaid';
        statusData[status] = (statusData[status] || 0) + amount;

        // Total calcs
        totalAmount += amount;
        invoiceCount++;

        // Calculate pending amount
        if (status === 'Unpaid' || status === 'Partially Paid') {
          pendingAmount += amount;
        }
      });

      // Prepare data for charts
      const monthsData = Object.entries(monthData).map(([month, amount]) => ({
        month,
        amount
      }));

      const clientsData = Object.entries(clientData)
        .map(([clientName, amount]) => ({ clientName, amount }))
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 5);

      const statusesData = Object.entries(statusData)
        .map(([status, amount]) => ({ status, amount }));

      // Set states
      setSalesByMonth(monthsData);
      setSalesByClient(clientsData);
      setPaymentStatus(statusesData);
      setTotalSales(totalAmount);
      setAverageSale(invoiceCount > 0 ? totalAmount / invoiceCount : 0);
      setTopClient(clientsData.length > 0 ? clientsData[0].clientName : 'None');
      setOutstandingAmount(pendingAmount);

    } catch (error) {
      console.error('Error fetching sales data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (event: SelectChangeEvent) => {
    setPeriod(event.target.value);
  };

  // Prepare data for charts
  const monthlyChartData = {
    labels: salesByMonth.map(item => item.month),
    datasets: [
      {
        label: 'Sales',
        data: salesByMonth.map(item => item.amount),
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
        tension: 0.4,
      },
    ],
  };

  const clientChartData = {
    labels: salesByClient.map(item => item.clientName),
    datasets: [
      {
        label: 'Sales by Client',
        data: salesByClient.map(item => item.amount),
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const statusChartData = {
    labels: paymentStatus.map(item => item.status),
    datasets: [
      {
        label: 'Payment Status',
        data: paymentStatus.map(item => item.amount),
        backgroundColor: [
          'rgba(75, 192, 192, 0.8)',
          'rgba(255, 99, 132, 0.8)',
          'rgba(255, 206, 86, 0.8)',
        ],
        borderColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(255, 206, 86, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  return (
    <AnimatedPage>
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 'bold',
                background: 'linear-gradient(90deg, #0D47A1 0%, #1976D2 50%, #42A5F5 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0px 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              Sales Insights
            </Typography>
            <Box display="flex" gap={2}>
              <FormControl sx={{ minWidth: 150 }} size="small">
                <InputLabel id="period-select-label">Time Period</InputLabel>
                <Select
                  labelId="period-select-label"
                  id="period-select"
                  value={period}
                  label="Time Period"
                  onChange={handlePeriodChange}
                >
                  <MenuItem value="3">Last 3 Months</MenuItem>
                  <MenuItem value="6">Last 6 Months</MenuItem>
                  <MenuItem value="12">Last 12 Months</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={fetchSalesData}
                sx={{
                  background: 'linear-gradient(90deg, #0D47A1 0%, #1976D2 100%)',
                  boxShadow: '0 4px 6px rgba(13, 71, 161, 0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #1565C0 0%, #42A5F5 100%)',
                    boxShadow: '0 6px 10px rgba(13, 71, 161, 0.3)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Key Metrics */}
              <Grid container spacing={3} mb={4}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.15) 0%, rgba(66, 165, 245, 0.05) 100%)',
                      border: '1px solid rgba(25, 118, 210, 0.1)',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      '&:hover': {
                        boxShadow: '0 8px 25px rgba(25, 118, 210, 0.15)',
                        transform: 'translateY(-5px)',
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" mb={2}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(25, 118, 210, 0.2)',
                          color: '#1976D2',
                          mr: 2
                        }}
                      >
                        <MonetizationOn fontSize="small" />
                      </Box>
                      <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                        Total Sales
                      </Typography>
                    </Box>

                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#0D47A1' }}>
                      {formatCurrency(totalSales)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.15) 0%, rgba(186, 104, 200, 0.05) 100%)',
                      border: '1px solid rgba(156, 39, 176, 0.1)',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      '&:hover': {
                        boxShadow: '0 8px 25px rgba(156, 39, 176, 0.15)',
                        transform: 'translateY(-5px)',
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" mb={2}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(156, 39, 176, 0.2)',
                          color: '#9C27B0',
                          mr: 2
                        }}
                      >
                        <TrendingUp fontSize="small" />
                      </Box>
                      <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                        Average Invoice Value
                      </Typography>
                    </Box>

                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#7B1FA2' }}>
                      {formatCurrency(averageSale)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(129, 199, 132, 0.05) 100%)',
                      border: '1px solid rgba(76, 175, 80, 0.1)',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      '&:hover': {
                        boxShadow: '0 8px 25px rgba(76, 175, 80, 0.15)',
                        transform: 'translateY(-5px)',
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" mb={2}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(76, 175, 80, 0.2)',
                          color: '#4CAF50',
                          mr: 2
                        }}
                      >
                        <Person fontSize="small" />
                      </Box>
                      <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                        Top Client
                      </Typography>
                    </Box>

                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#2E7D32', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                      {topClient}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, rgba(255, 87, 34, 0.15) 0%, rgba(255, 112, 67, 0.05) 100%)',
                      border: '1px solid rgba(255, 87, 34, 0.1)',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      '&:hover': {
                        boxShadow: '0 8px 25px rgba(255, 87, 34, 0.15)',
                        transform: 'translateY(-5px)',
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" mb={2}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255, 87, 34, 0.2)',
                          color: '#FF5722',
                          mr: 2
                        }}
                      >
                        <CreditCard fontSize="small" />
                      </Box>
                      <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                        Outstanding Amount
                      </Typography>
                    </Box>

                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#D84315' }}>
                      {formatCurrency(outstandingAmount)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Charts */}
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 4,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                      border: '1px solid rgba(0,0,0,0.05)',
                    }}
                  >
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                        mb: 3,
                        color: theme.palette.primary.main
                      }}
                    >
                      Monthly Sales Trend
                    </Typography>
                    <Box height="300px">
                      {salesByMonth.length > 0 ? (
                        <Line
                          data={monthlyChartData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            scales: {
                              y: {
                                beginAtZero: true,
                                ticks: {
                                  callback: function (value) {
                                    return '₹' + value.toLocaleString('en-IN');
                                  }
                                },
                                grid: {
                                  display: true,
                                  color: 'rgba(0,0,0,0.05)'
                                }
                              },
                              x: {
                                grid: {
                                  display: false
                                }
                              }
                            },
                            plugins: {
                              legend: {
                                position: 'top',
                                align: 'end'
                              },
                              tooltip: {
                                backgroundColor: 'rgba(0,0,0,0.8)',
                                padding: 12,
                                usePointStyle: true
                              }
                            }
                          }}
                        />
                      ) : (
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                          <Typography variant="body1" color="textSecondary">
                            No sales data available for this period
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 4,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                      border: '1px solid rgba(0,0,0,0.05)',
                    }}
                  >
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                        mb: 3,
                        color: theme.palette.primary.main
                      }}
                    >
                      Payment Status Distribution
                    </Typography>
                    <Box height="300px" display="flex" justifyContent="center" alignItems="center">
                      {paymentStatus.length > 0 ? (
                        <Doughnut
                          data={statusChartData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: 'bottom',
                                labels: {
                                  usePointStyle: true,
                                  padding: 20,
                                  font: {
                                    size: 12
                                  }
                                }
                              },
                              tooltip: {
                                backgroundColor: 'rgba(0,0,0,0.8)',
                                padding: 12,
                                callbacks: {
                                  label: function (context) {
                                    const label = context.label || '';
                                    const value = context.formattedValue || '';
                                    const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                                    const percentage = Math.round((context.raw as number) / total * 100);
                                    return `${label}: ${value} (${percentage}%)`;
                                  }
                                }
                              }
                            },
                            cutout: '65%'
                          }}
                        />
                      ) : (
                        <Typography variant="body1" color="textSecondary">
                          No payment data available
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 4,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                      border: '1px solid rgba(0,0,0,0.05)',
                    }}
                  >
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                        mb: 3,
                        color: theme.palette.primary.main
                      }}
                    >
                      Top 5 Clients by Revenue
                    </Typography>
                    <Box height="300px">
                      {salesByClient.length > 0 ? (
                        <Bar
                          data={clientChartData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            indexAxis: 'y',
                            scales: {
                              x: {
                                beginAtZero: true,
                                ticks: {
                                  callback: function (value) {
                                    return '₹' + value.toLocaleString('en-IN');
                                  }
                                },
                                grid: {
                                  display: true,
                                  color: 'rgba(0,0,0,0.05)'
                                }
                              },
                              y: {
                                grid: {
                                  display: false
                                }
                              }
                            },
                            plugins: {
                              legend: {
                                display: false
                              },
                              tooltip: {
                                backgroundColor: 'rgba(0,0,0,0.8)',
                                padding: 12
                              }
                            }
                          }}
                        />
                      ) : (
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                          <Typography variant="body1" color="textSecondary">
                            No client data available
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      </Container>
    </AnimatedPage>
  );
};

export default SalesInsights; 
